import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  HourglassEmpty,
  RadioButtonUnchecked,
  WhatsApp,
  ExpandMore,
  Warning,
  Schedule,
} from '@mui/icons-material';
import UPIQRDialog from './UPIQRDialog';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

const getStatusChip = (status) => {
  switch (status) {
    case 'Completed':
      return (
        <Chip
          icon={<CheckCircle />}
          label='Completed'
          color='success'
          size='small'
          sx={{ borderRadius: '16px' }}
        />
      );
    case 'In Progress':
      return (
        <Chip
          icon={<HourglassEmpty />}
          label='In Progress'
          color='warning'
          size='small'
          sx={{ borderRadius: '16px' }}
        />
      );
    default:
      return (
        <Chip
          icon={<RadioButtonUnchecked />}
          label='Yet to begin'
          color='default'
          size='small'
          sx={{ borderRadius: '16px' }}
        />
      );
  }
};

const getDelayedChip = () => {
  return (
    <Chip
      icon={<Warning />}
      label='Delayed'
      color='error'
      size='small'
      sx={{ ml: 1, borderRadius: '16px' }}
    />
  );
};

const getPaymentStatusChip = () => {
  return (
    <Chip
      icon={<RadioButtonUnchecked />}
      label='Payment pending'
      color='default'
      size='small'
      sx={{ ml: 1, borderRadius: '16px' }}
    />
  );
};

const calculateDaysSpent = (step) => {
  const today = new Date();
  const startDate = step.actualStartDate
    ? new Date(step.actualStartDate)
    : null;
  const endDate = step.actualCompletionDate
    ? new Date(step.actualCompletionDate)
    : null;

  if (step.status === 'Completed' && startDate && endDate) {
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } else if (step.status === 'In Progress' && startDate) {
    const diffTime = Math.abs(today - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  return null;
};

const isStepDelayed = (step) => {
  if (step.status === 'Completed') return false;

  const today = new Date();
  const expectedEndDate = new Date(step.estimatedCompletionDate);

  return today > expectedEndDate;
};

const getContactInfo = (owner) => {
  return { owner };
};

const getPaymentMilestone = (stepId) => {
  return null;
};

const formatDateRange = (start, end) => {
  const s = new Date(start).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
  const e = new Date(end).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
  return `${s} – ${e}`;
};

const formatDate = (start) => {
  const s = new Date(start).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return `${s}`;
};

const ProjectTracking = ({ projectTrackingData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  if (
    !projectTrackingData ||
    !Array.isArray(projectTrackingData.steps) ||
    projectTrackingData.steps.length === 0
  ) {
    return (
      <Typography variant='body1' color='text.secondary'>
        No project tracking information available.
      </Typography>
    );
  }
  const { steps, startDate } = projectTrackingData;
  // Calculate projectEndDate as 68 days after startDate
  const projectEndDate = (() => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + 68);
    return d;
  })();

  const upiId = 'vaysolar@icici';
  const amount = '';
  const note = 'VaySolar Payment';

  const openUPIApp = () => {
    const upiUrl = `upi://pay?pa=${upiId}&pn=VaySolar&tn=${note}${amount ? `&am=${amount}` : ''}`;

    try {
      window.location.href = upiUrl;
      setTimeout(() => {
        setOpen(true);
      }, 1500);
    } catch (error) {
      setOpen(true);
    }
  };

  return (
    <Stack variant='outlined' sx={{ width: '100%', maxWidth: 700, mb: 4 }}>
      <Typography variant='h5' fontWeight={600}>
        Project tracking
      </Typography>

      {/* Project completion date + Payment button */}
      <Box sx={{ mt: 4, borderRadius: 2 }}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Stack direction='row' alignItems='flex-start' spacing={1}>
            <Schedule color='primary' />
            <Typography variant='h6' color='primary'>
              Expected completion by{' '}
              <strong>
                {new Date(projectEndDate).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </strong>
            </Typography>
          </Stack>

          <Stack spacing={1} alignItems='flex-end'>
            {isMobile ? (
              <Button
                variant='contained'
                onClick={openUPIApp}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                }}
              >
                pay
              </Button>
            ) : (
              <Button
                sx={{ textTransform: 'none' }}
                variant='outlined'
                onClick={() => setOpen(true)}
                startIcon={<CurrencyRupeeIcon />}
              >
                Open QR scanner
              </Button>
            )}
            <UPIQRDialog open={open} onClose={() => setOpen(false)} />
          </Stack>
        </Stack>
      </Box>

      {/* Project steps */}
      <Box sx={{ mt: 2 }}>
        {steps.map((step) => {
          const daysSpent = calculateDaysSpent(step);
          const isDelayed = isStepDelayed(step);

          return (
            <Accordion
              key={step.stepId}
              expanded={expanded === step.stepId}
              onChange={handleChange(step.stepId)}
              elevation={0}
              sx={{
                mt: 1,
                maxWidth: '840px',
                borderRadius: '10px !important',
                border: '1px solid lightgray',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-content': {
                  margin: { xs: '10px 0', sm: '12px 0' },
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack
                  direction='row'
                  alignItems='flex-start'
                  spacing={0.5}
                  width='100%'
                >
                  <Typography
                    variant='body1'
                    fontWeight={600}
                    sx={{ flexGrow: 1 }}
                  >
                    {step.stepLabel}
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      display='block'
                      mt={0.5}
                    >
                      {expanded !== step.stepId ? (
                        <>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            display='block'
                            mt={0.5}
                          >
                            Estimated completion by{' '}
                            <strong>
                              {formatDate(step.estimatedCompletionDate)}
                            </strong>
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          display='block'
                          mt={0.5}
                        >
                          Upto{' '}
                          <strong>{step.estimatedDaysToComplete} days</strong>{' '}
                          to complete
                        </Typography>
                      )}
                    </Typography>
                  </Typography>

                  <Stack
                    direction='row'
                    flexDirection={isMobile ? 'column' : 'row'}
                    gap={1}
                    alignItems='center'
                  >
                    {step?.isPaymentMilestone &&
                      step.status === 'Yet to begin' &&
                      getPaymentStatusChip()}
                    {getStatusChip(step.status)}
                    {isDelayed && getDelayedChip()}
                  </Stack>
                </Stack>
              </AccordionSummary>

              <AccordionDetails>
                <Stack gap={0.5}>
                  {step.owner && (
                    <Box>
                      <Typography variant='subtitle2' fontWeight={600}>
                        Responsible team: {step.owner}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant='body2' color='text.secondary'>
                    {step.stepDescription}
                  </Typography>

                  <Box>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      display='block'
                    >
                      Estimated completion by:{' '}
                      <strong>
                        {formatDate(step.estimatedCompletionDate)}
                      </strong>
                    </Typography>

                    {step.actualStartDate && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                      >
                        <strong>Actual start:</strong>{' '}
                        {new Date(step.actualStartDate).toLocaleDateString(
                          'en-GB',
                          {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          },
                        )}
                      </Typography>
                    )}

                    {step.actualCompletionDate && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                      >
                        <strong>Actual completion:</strong>{' '}
                        {new Date(step.actualCompletionDate).toLocaleDateString(
                          'en-GB',
                          {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          },
                        )}
                      </Typography>
                    )}

                    {daysSpent >= 0 && step.actualCompletionDate && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                        fontWeight={600}
                      >
                        <strong>Days spent:</strong> {daysSpent} days
                      </Typography>
                    )}
                  </Box>

                  {step.dependencies?.length > 0 && (
                    <Typography variant='caption' color='text.secondary'>
                      Step only begins after:{' '}
                      <strong>{step.dependencies.join(', ')}</strong>
                    </Typography>
                  )}

                  {step.stepId === 'loan_sanction' && (
                    <Box>
                      <Button
                        variant='contained'
                        startIcon={<WhatsApp />}
                        size='small'
                        component={Link}
                        href='https://wa.me/918296378206'
                        target='_blank'
                        color='success'
                        sx={{ textTransform: 'none' }}
                      >
                        Chat with VaySolar finance team
                      </Button>
                    </Box>
                  )}

                  {step?.isPaymentMilestone && (
                    <Stack spacing={1} alignItems='flex-end'>
                      {isMobile ? (
                        <Button
                          variant='contained'
                          onClick={openUPIApp}
                          sx={{ textTransform: 'none', px: 3, py: 1 }}
                        >
                          pay
                        </Button>
                      ) : (
                        <Button
                          sx={{ textTransform: 'none' }}
                          variant='outlined'
                          onClick={() => setOpen(true)}
                          startIcon={<CurrencyRupeeIcon />}
                        >
                          Open QR scanner
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Stack>
  );
};

export default ProjectTracking;
